import React, { useCallback, useEffect, useState } from "react";
import { Card, Spinner, Form, Row, Col } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync, faFileDownload } from "@fortawesome/free-solid-svg-icons";
import { Button, ButtonGroup } from "@themesberg/react-bootstrap";
import { postApi, fetchApi } from "../../../redux/api";
import { formatNumber } from "../../../redux/utils";
import moment from "moment";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

const boldRight = { textAlign: "right", fontWeight: "bold" };
const normalRight = { textAlign: "right" };
const boldLeft = { fontWeight: "bold" };

export default function BudgetPerformance() {
  const [results, setResults] = useState(null);
  const [openingBal, setOpeningBal] = useState({});
  const [form, setForm] = useState({ budget_year: moment().format("YYYY") });
  const [loading, setLoading] = useState(false);

  const years = Array.from({ length: 10 }, (_, i) => moment().format("YYYY") - i);

  const getOpeningBalance = useCallback(() => {
    postApi(`opening_balance?query_type=select&budget_year=${form.budget_year}`, {
      budget_year: form.budget_year,
    })
      .then((data) => {
        setOpeningBal(data?.results?.[0] || {});
      })
      .catch(() => { });
  }, [form.budget_year]);

  useEffect(() => {
    getOpeningBalance();
  }, [getOpeningBalance]);

  useEffect(() => {
    setLoading(true);
    fetchApi(`get-report?query_type=budget_overview&budget_year=${form.budget_year}`)
      .then((data) => {
        setResults(data?.results?.[0] || null);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [form.budget_year]);

  const exportToExcel = () => {
    if (!results) return;
    const rows = [
      { Description: "Opening Balance", [`${parseInt(form.budget_year) - 2} Full Year Actual`]: openingBal?.prev_year2_amt, [`${parseInt(form.budget_year) - 1} Approved Budget`]: openingBal?.prev_year1_amt, [`${parseInt(form.budget_year) - 1} Performance Jan-Aug`]: openingBal?.prev_year1_closing, [`${form.budget_year} Proposed Budget`]: openingBal?.opening_balance, [`${form.budget_year} Approved Budget`]: openingBal?.opening_balance },
      { Description: "Recurrent Revenue" },
      { Description: "11 - GOVERNMENT SHARE OF FAAC", [`${parseInt(form.budget_year) - 2} Full Year Actual`]: results?.prev_year2_actual_amount_fac, [`${parseInt(form.budget_year) - 1} Approved Budget`]: results?.prev_year1_approve_amount_fac, [`${parseInt(form.budget_year) - 1} Performance Jan-Aug`]: results?.prev_year1_actual_amount_fac, [`${form.budget_year} Proposed Budget`]: results?.budget_amount_fac, [`${form.budget_year} Approved Budget`]: results?.approve_amount_fac },
      { Description: "12 - INDEPENDENT REVENUE", [`${parseInt(form.budget_year) - 2} Full Year Actual`]: results?.prev_year2_actual_amount_igr, [`${parseInt(form.budget_year) - 1} Approved Budget`]: results?.prev_year1_approve_amount_igr, [`${parseInt(form.budget_year) - 1} Performance Jan-Aug`]: results?.prev_year1_actual_amount_igr, [`${form.budget_year} Proposed Budget`]: results?.budget_amount_igr, [`${form.budget_year} Approved Budget`]: results?.approve_amount_igr },
      { Description: "Recurrent Expenditure" },
      { Description: "21 - PERSONNEL COST", [`${parseInt(form.budget_year) - 2} Full Year Actual`]: results?.prev_year2_actual_amount_pers, [`${parseInt(form.budget_year) - 1} Approved Budget`]: results?.prev_year1_approve_amount_pers, [`${parseInt(form.budget_year) - 1} Performance Jan-Aug`]: results?.prev_year1_actual_amount_pers, [`${form.budget_year} Proposed Budget`]: results?.budget_amount_pers, [`${form.budget_year} Approved Budget`]: results?.approve_amount_pers },
      { Description: "22 - OTHER RECURRENT COST", [`${parseInt(form.budget_year) - 2} Full Year Actual`]: results?.prev_year2_actual_amount_over, [`${parseInt(form.budget_year) - 1} Approved Budget`]: results?.prev_year1_approve_amount_over, [`${parseInt(form.budget_year) - 1} Performance Jan-Aug`]: results?.prev_year1_actual_amount_over, [`${form.budget_year} Proposed Budget`]: results?.budget_amount_over, [`${form.budget_year} Approved Budget`]: results?.approve_amount_over },
      { Description: "Capital Receipt" },
      { Description: "13 - AID AND GRANT", [`${parseInt(form.budget_year) - 2} Full Year Actual`]: results?.prev_year2_actual_amount_aid, [`${parseInt(form.budget_year) - 1} Approved Budget`]: results?.prev_year1_approve_amount_aid, [`${parseInt(form.budget_year) - 1} Performance Jan-Aug`]: results?.prev_year1_actual_amount_aid, [`${form.budget_year} Proposed Budget`]: results?.budget_amount_aid, [`${form.budget_year} Approved Budget`]: results?.approve_amount_aid },
      { Description: "14 - CAPITAL DEVELOPMENT FUND (CDF) RECEIPTS", [`${parseInt(form.budget_year) - 2} Full Year Actual`]: results?.prev_year2_actual_amount_cdf, [`${parseInt(form.budget_year) - 1} Approved Budget`]: results?.prev_year1_approve_amount_cdf, [`${parseInt(form.budget_year) - 1} Performance Jan-Aug`]: results?.prev_year1_actual_amount_cdf, [`${form.budget_year} Proposed Budget`]: results?.budget_amount_cdf, [`${form.budget_year} Approved Budget`]: results?.approve_amount_cdf },
      { Description: "32 - Capital Expenditure", [`${parseInt(form.budget_year) - 2} Full Year Actual`]: results?.prev_year2_actual_amount_cap, [`${parseInt(form.budget_year) - 1} Approved Budget`]: results?.prev_year1_approve_amount_cap, [`${parseInt(form.budget_year) - 1} Performance Jan-Aug`]: results?.prev_year1_actual_amount_cap, [`${form.budget_year} Proposed Budget`]: results?.budget_amount_cap, [`${form.budget_year} Approved Budget`]: results?.approve_amount_cap },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Budget Performance");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    FileSaver.saveAs(new Blob([buf], { type: "application/octet-stream" }), `Budget_Performance_${form.budget_year}.xlsx`);
  };

  const Cell = ({ value, style }) => <td style={style}>{formatNumber(value)}</td>;
  const emptyRow = () => (
    <tr style={{ fontSize: "13px" }}>
      <td></td><td></td><td></td><td></td><td></td><td></td>
    </tr>
  );

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Budget Overview</h4>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0">
          <ButtonGroup>
            <Button variant="outline-primary" size="sm" onClick={() => setForm({ budget_year: moment().format("YYYY") })}>
              <FontAwesomeIcon icon={faSync} className="me-1" /> Reset
            </Button>
            <Button variant="outline-primary" size="sm" onClick={exportToExcel} disabled={!results}>
              <FontAwesomeIcon icon={faFileDownload} className="me-1" /> Export
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Budget Year</Form.Label>
            <Form.Select
              value={form.budget_year}
              onChange={(e) => setForm((p) => ({ ...p, budget_year: e.target.value }))}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Card.Body className="p-0" style={{ overflow: "auto" }}>
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <table className="table table-striped table-bordered" style={{ fontSize: "13px", minWidth: 900 }}>
              <thead style={{ backgroundColor: "orange", textAlign: "center" }}>
                <tr>
                  <th style={{ backgroundColor: "orange" }}>Description</th>
                  <th style={{ backgroundColor: "orange" }}>{parseInt(form.budget_year) - 2} Full Year Actual (₦)</th>
                  <th style={{ backgroundColor: "orange" }}>{parseInt(form.budget_year) - 1} Approved Budget (₦)</th>
                  <th style={{ backgroundColor: "orange" }}>{parseInt(form.budget_year) - 1} Performance Jan To Aug (₦)</th>
                  <th style={{ backgroundColor: "orange" }}>{form.budget_year} Proposed Budget (₦)</th>
                  <th style={{ backgroundColor: "orange" }}>{form.budget_year} Approved Budget (₦)</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ fontSize: "13px" }}>
                  <td style={boldLeft}>Opening Balance</td>
                  <Cell value={openingBal?.prev_year2_amt} style={boldRight} />
                  <Cell value={openingBal?.prev_year1_amt} style={boldRight} />
                  <Cell value={openingBal?.prev_year1_closing} style={boldRight} />
                  <Cell value={openingBal?.opening_balance} style={boldRight} />
                  <Cell value={openingBal?.opening_balance} style={boldRight} />
                </tr>
                <tr style={{ fontSize: "13px" }}>
                  <td style={boldLeft}>Recurrent Revenue</td>
                  <Cell value={results?.prev_year2_actual_amount_rec_revenue} style={boldRight} />
                  <Cell value={results?.prev_year1_approve_amount_rec_revenue} style={boldRight} />
                  <Cell value={results?.prev_year1_actual_amount_rec_revenue} style={boldRight} />
                  <Cell value={results?.budget_amount_rec_revenue} style={boldRight} />
                  <Cell value={results?.approve_amount_rec_revenue} style={boldRight} />
                </tr>
                <tr style={{ fontSize: "13px" }}>
                  <td>11 - GOVERNMENT SHARE OF FAAC</td>
                  <Cell value={results?.prev_year2_actual_amount_fac} style={normalRight} />
                  <Cell value={results?.prev_year1_approve_amount_fac} style={normalRight} />
                  <Cell value={results?.prev_year1_actual_amount_fac} style={normalRight} />
                  <Cell value={results?.budget_amount_fac} style={normalRight} />
                  <Cell value={results?.approve_amount_fac} style={normalRight} />
                </tr>
                <tr style={{ fontSize: "13px" }}>
                  <td>12 - INDEPENDENT REVENUE</td>
                  <Cell value={results?.prev_year2_actual_amount_igr} style={normalRight} />
                  <Cell value={results?.prev_year1_approve_amount_igr} style={normalRight} />
                  <Cell value={results?.prev_year1_actual_amount_igr} style={normalRight} />
                  <Cell value={results?.budget_amount_igr} style={normalRight} />
                  <Cell value={results?.approve_amount_igr} style={normalRight} />
                </tr>
                {emptyRow()}
                <tr style={{ fontSize: "13px" }}>
                  <td style={boldLeft}>Recurrent Expenditure</td>
                  <Cell value={results?.prev_year2_actual_amount_rec_exp} style={boldRight} />
                  <Cell value={results?.prev_year1_approve_amount_rec_exp} style={boldRight} />
                  <Cell value={results?.prev_year1_actual_amount_rec_exp} style={boldRight} />
                  <Cell value={results?.budget_amount_rec_exp} style={boldRight} />
                  <Cell value={results?.approve_amount_rec_exp} style={boldRight} />
                </tr>
                <tr style={{ fontSize: "13px" }}>
                  <td>21 - PERSONNEL COST</td>
                  <Cell value={results?.prev_year2_actual_amount_pers} style={normalRight} />
                  <Cell value={results?.prev_year1_approve_amount_pers} style={normalRight} />
                  <Cell value={results?.prev_year1_actual_amount_pers} style={normalRight} />
                  <Cell value={results?.budget_amount_pers} style={normalRight} />
                  <Cell value={results?.approve_amount_pers} style={normalRight} />
                </tr>
                <tr style={{ fontSize: "13px" }}>
                  <td>22 - OTHER RECURRENT COST</td>
                  <Cell value={results?.prev_year2_actual_amount_over} style={normalRight} />
                  <Cell value={results?.prev_year1_approve_amount_over} style={normalRight} />
                  <Cell value={results?.prev_year1_actual_amount_over} style={normalRight} />
                  <Cell value={results?.budget_amount_over} style={normalRight} />
                  <Cell value={results?.approve_amount_over} style={normalRight} />
                </tr>
                {emptyRow()}
                <tr style={{ fontSize: "13px" }}>
                  <td style={boldLeft}>Transfer to Capital Account</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.prev_year2_actual_amount_rec_revenue || 0) + (openingBal?.prev_year2_amt || 0) - (results?.prev_year2_actual_amount_rec_exp || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.prev_year1_approve_amount_rec_revenue || 0) + (openingBal?.prev_year1_amt || 0) - (results?.prev_year1_approve_amount_rec_exp || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.prev_year1_actual_amount_rec_revenue || 0) + (openingBal?.prev_year1_closing || 0) - (results?.prev_year1_actual_amount_rec_exp || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.budget_amount_rec_revenue || 0) + (openingBal?.opening_balance || 0) - (results?.budget_amount_rec_exp || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.approve_amount_rec_revenue || 0) + (openingBal?.opening_balance || 0) - (results?.approve_amount_rec_exp || 0)
                  )}</td>
                </tr>
                {emptyRow()}
                <tr style={{ fontSize: "13px" }}>
                  <td style={boldLeft}>Capital Receipt</td>
                  <Cell value={results?.prev_year2_actual_amount_cap_rec} style={boldRight} />
                  <Cell value={results?.prev_year1_approve_amount_cap_rec} style={boldRight} />
                  <Cell value={results?.prev_year1_actual_amount_cap_rec} style={boldRight} />
                  <Cell value={results?.budget_amount_cap_rec} style={boldRight} />
                  <Cell value={results?.approve_amount_cap_rec} style={boldRight} />
                </tr>
                <tr style={{ fontSize: "13px" }}>
                  <td>13 - AID AND GRANT</td>
                  <Cell value={results?.prev_year2_actual_amount_aid} style={normalRight} />
                  <Cell value={results?.prev_year1_approve_amount_aid} style={normalRight} />
                  <Cell value={results?.prev_year1_actual_amount_aid} style={normalRight} />
                  <Cell value={results?.budget_amount_aid} style={normalRight} />
                  <Cell value={results?.approve_amount_aid} style={normalRight} />
                </tr>
                <tr style={{ fontSize: "13px" }}>
                  <td>14 - CAPITAL DEVELOPMENT FUND (CDF) RECEIPTS</td>
                  <Cell value={results?.prev_year2_actual_amount_cdf} style={normalRight} />
                  <Cell value={results?.prev_year1_approve_amount_cdf} style={normalRight} />
                  <Cell value={results?.prev_year1_actual_amount_cdf} style={normalRight} />
                  <Cell value={results?.budget_amount_cdf} style={normalRight} />
                  <Cell value={results?.approve_amount_cdf} style={normalRight} />
                </tr>
                {emptyRow()}
                <tr style={{ fontSize: "13px" }}>
                  <td style={boldLeft}>32 - Capital Expenditure</td>
                  <Cell value={results?.prev_year2_actual_amount_cap} style={boldRight} />
                  <Cell value={results?.prev_year1_approve_amount_cap} style={boldRight} />
                  <Cell value={results?.prev_year1_actual_amount_cap} style={boldRight} />
                  <Cell value={results?.budget_amount_cap} style={boldRight} />
                  <Cell value={results?.approve_amount_cap} style={boldRight} />
                </tr>
                {emptyRow()}
                <tr style={{ fontSize: "13px" }}>
                  <td style={boldLeft}>Total Revenue (including OB)</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.prev_year2_amt || 0) + (results?.prev_year2_actual_amount_rec_revenue || 0) + (results?.prev_year2_actual_amount_cap_rec || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.prev_year1_amt || 0) + (results?.prev_year1_approve_amount_rec_revenue || 0) + (results?.prev_year1_approve_amount_cap_rec || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.prev_year1_closing || 0) + (results?.prev_year1_actual_amount_rec_revenue || 0) + (results?.prev_year1_actual_amount_cap_rec || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.opening_balance || 0) + (results?.budget_amount_rec_revenue || 0) + (results?.budget_amount_cap_rec || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.opening_balance || 0) + (results?.approve_amount_rec_revenue || 0) + (results?.approve_amount_cap_rec || 0)
                  )}</td>
                </tr>
                <tr style={{ fontSize: "13px" }}>
                  <td style={boldLeft}>Total Expenditure</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.prev_year2_actual_amount_rec_exp || 0) + (results?.prev_year2_actual_amount_cap || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.prev_year1_approve_amount_rec_exp || 0) + (results?.prev_year1_approve_amount_cap || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.prev_year1_actual_amount_rec_exp || 0) + (results?.prev_year1_actual_amount_cap || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.budget_amount_rec_exp || 0) + (results?.budget_amount_cap || 0)
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (results?.approve_amount_rec_exp || 0) + (results?.approve_amount_cap || 0)
                  )}</td>
                </tr>
                {emptyRow()}
                <tr style={{ fontSize: "13px" }}>
                  <td style={boldLeft}>Closing Balance</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.prev_year2_amt || 0) + (results?.prev_year2_actual_amount_rec_revenue || 0) + (results?.prev_year2_actual_amount_cap_rec || 0) -
                    ((results?.prev_year2_actual_amount_rec_exp || 0) + (results?.prev_year2_actual_amount_cap || 0))
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.prev_year1_amt || 0) + (results?.prev_year1_approve_amount_rec_revenue || 0) + (results?.prev_year1_approve_amount_cap_rec || 0) -
                    ((results?.prev_year1_approve_amount_rec_exp || 0) + (results?.prev_year1_approve_amount_cap || 0))
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.prev_year1_closing || 0) + (results?.prev_year1_actual_amount_rec_revenue || 0) + (results?.prev_year1_actual_amount_cap_rec || 0) -
                    ((results?.prev_year1_actual_amount_rec_exp || 0) + (results?.prev_year1_actual_amount_cap || 0))
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.opening_balance || 0) + (results?.budget_amount_rec_revenue || 0) + (results?.budget_amount_cap_rec || 0) -
                    ((results?.budget_amount_rec_exp || 0) + (results?.budget_amount_cap || 0))
                  )}</td>
                  <td style={boldRight}>{formatNumber(
                    (openingBal?.opening_balance || 0) + (results?.approve_amount_rec_revenue || 0) + (results?.approve_amount_cap_rec || 0) -
                    ((results?.approve_amount_rec_exp || 0) + (results?.approve_amount_cap || 0))
                  )}</td>
                </tr>
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>
    </>
  );
}